// assets/stz-gift-toggle.js
// "This is a gift" on The Book of You PDP (blocks/gift-toggle.liquid). Ticked: the birth-data
// blocks are hidden and their inputs disabled with `name` removed (the disable-and-hide shape from
// starlyzed-birth-fields.js), so birth-data-gate.js, age-fit.js and chart-preview.js see no birth
// fields; the recipient fields appear and the hidden gift properties are enabled. Unticked: all of
// it is restored. The recipient's name is required in gift mode, checked in the capture phase with
// an inline error, never an alert (same pattern as birth-data-gate.js / stz-ai-acknowledgement.js).

const BIRTH_BLOCKS = 'product-custom-property-component, chart-preview';

class GiftToggle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.box = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gift-toggle]'));
    this.fields = /** @type {HTMLElement | null} */ (this.querySelector('[data-gift-fields]'));
    this.error = /** @type {HTMLElement | null} */ (this.querySelector('[data-gift-error]'));
    this.giftInputs = /** @type {NodeListOf<HTMLInputElement | HTMLTextAreaElement>} */ (this.querySelectorAll('[data-gift-input]'));
    const formId = this.dataset.formId;
    this.form =
      (formId && document.getElementById(formId)) ||
      this.closest('product-form-component')?.querySelector('form') ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    /** @type {Map<Element, { name: string | null, required: boolean, disabled: boolean }>} */
    this.saved = new Map();
    if (!this.box) return;
    this.box.addEventListener('change', () => this.apply(this.box.checked));
    this.form?.addEventListener('submit', (e) => this.onSubmit(e), true);
    this.apply(this.box.checked);
  }

  /** Every birth-data block on the page, resolved at toggle time (blocks render after this one). */
  birthBlocks() {
    return Array.from(document.querySelectorAll(BIRTH_BLOCKS)).filter((el) => !this.contains(el));
  }

  /** @param {boolean} gift */
  apply(gift) {
    for (const block of this.birthBlocks()) {
      for (const field of block.querySelectorAll('input, textarea, select')) {
        if (gift) {
          if (!this.saved.has(field)) {
            this.saved.set(field, { name: field.getAttribute('name'), required: field.required, disabled: field.disabled });
          }
          field.required = false;
          field.removeAttribute('required');
          field.removeAttribute('aria-required');
          field.removeAttribute('name');
          field.disabled = true;
        } else {
          const was = this.saved.get(field);
          if (!was) continue;
          if (was.name !== null) field.setAttribute('name', was.name);
          field.required = was.required;
          if (was.required) field.setAttribute('aria-required', 'true');
          field.disabled = was.disabled;
          this.saved.delete(field);
        }
      }
      /** @type {HTMLElement} */ (block).hidden = gift;
    }
    for (const input of this.giftInputs) input.disabled = !gift;
    if (this.fields) this.fields.hidden = !gift;
    if (!gift && this.error) this.error.hidden = true;
    // let the gate / preview / age-fit re-read the page (they listen for input at the document)
    document.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /** @param {SubmitEvent} e */
  onSubmit(e) {
    if (!this.box?.checked) return;
    const name = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-gift-required]'));
    if (name && name.value.trim().length > 0) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.error) this.error.hidden = false;
    name?.focus();
  }
}

if (!customElements.get('stz-gift-toggle')) customElements.define('stz-gift-toggle', GiftToggle);
